import { useState } from 'react';

import Button from '@/components/Button/Button';

import useCurrentHome from '../hooks/useCurrentHome';
import useUnknownSensors from '../hooks/useUnknownSensors';
import { formatTimestamp } from '../lib/format';
import type { UnknownSensor } from '../types/unknownSensor';
import AsyncSection from './AsyncSection';
import NewSensorModal from './NewSensorModal';

function fmtNum(value: number | null, suffix: string, digits = 1): string {
  if (value === null || !Number.isFinite(value)) return '—';
  return `${value.toFixed(digits)} ${suffix}`;
}

export default function UnknownSensorsSection() {
  const homeQuery = useCurrentHome();
  const homeId = homeQuery.data?.id;
  const list = useUnknownSensors(homeId);
  const [selected, setSelected] = useState<UnknownSensor | null>(null);

  const loading = homeQuery.isPending || list.isPending;
  const error = homeQuery.isError || !homeId || list.isError;
  const errorMessage = homeQuery.isError || !homeId ? 'Failed to load home.' : 'Failed to load unknown sensors.';
  const retry = () => {
    if (homeQuery.isError) void homeQuery.refetch();
    if (list.isError) void list.refetch();
  };

  const items = list.data ?? [];

  return (
    <AsyncSection loading={loading} error={error} errorMessage={errorMessage} onRetry={retry}>
      <div className="space-y-3">
        <p className="text-sm text-(--text-secondary)">
          Sensors seen by an ingestion source but not yet configured. Promote one into a real sensor with <strong>Create sensor</strong>.
        </p>
        {items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-(--border-default) bg-(--bg-surface) px-4 py-6 text-center text-sm text-(--text-secondary)">
            No unknown sensors yet. Once a configured ingestion URL receives data, new physical ids appear here.
          </div>
        ) : (
          <ul className="divide-y divide-(--border-subtle) overflow-hidden rounded-lg border border-(--border-subtle) bg-(--bg-surface)">
            {items.map((item) => (
              <li key={item.id} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <code className="font-mono text-sm text-(--text-primary)">{item.physicalId}</code>
                    <span className="text-xs text-(--text-tertiary)">{item.source}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-(--text-secondary)">
                    <span>temp {fmtNum(item.lastPayloadSummary.temperatureC, '°C')}</span>
                    <span>humidity {fmtNum(item.lastPayloadSummary.humidityPct, '%', 0)}</span>
                    <span>rssi {fmtNum(item.lastPayloadSummary.rssi, 'dBm', 0)}</span>
                    <span>seen {item.seenCount}×</span>
                  </div>
                  <div className="text-xs text-(--text-tertiary)">
                    Last seen {formatTimestamp(item.lastSeenAt)} · first seen {formatTimestamp(item.firstSeenAt)}
                  </div>
                </div>
                {homeId && (
                  <div className="shrink-0">
                    <Button size="sm" variant="primary" onClick={() => setSelected(item)}>
                      Create sensor
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        {homeId && <NewSensorModal open={selected !== null} homeId={homeId} unknown={selected} onClose={() => setSelected(null)} />}
      </div>
    </AsyncSection>
  );
}
